<template>
  <div class="c-quick-filters">
    <NH6 v-if="!hideTitle" class="mb-2">{{ title || t('canvasUI.CQuickFilters.defaultTitle') }}</NH6>
    <div class="flex flex-wrap gap-2">
      <NTag
        v-for="f in availableFilters"
        :key="f.name"
        :bordered="activeFilters[f.name]"
        class="c-quick-filters__tag select-none b-radius-7 cursor-pointer max-w-full overflow-hidden text-ellipsis whitespace-nowrap"
        :class="{
          'c-quick-filters__tag--active': activeFilters[f.name],
          '!leading-[12px] !text-[10px] !tracking-[0.5px] px-2': size === 'small',
          'px-4': size !== 'small',
        }"
        :closable="activeFilters[f.name] && !mutuallyExclusive"
        round
        :size="size === 'small' ? 'small' : 'medium'"
        type="primary"
        @click="onTagClick(f)"
        @close="onTagClose(f)"
      >
        {{ f.displayName }}
      </NTag>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ComputedRef } from 'vue'
import { ref, watchEffect, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { NTag, NH6 } from 'naive-ui'
import type { AvailableFilters } from '@lib/components/CQuickFilters/CQuickFilters.types'
import type { ArrayElement } from '@lib/types/ts'
import useThemeVars from '@lib/composables/useThemeVars'

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    title?: string
    availableFilters: AvailableFilters
    modelValue?: string[] | null
    mutuallyExclusive?: boolean // make it behave like tabs
    size?: 'default' | 'small'
    hideTitle?: boolean
  }>(),
  {
    mutuallyExclusive: false,
    size: 'default',
    hideTitle: false,
  },
)
const emit = defineEmits(['update:model-value'])
const selectedFilters = ref<string[]>(props.modelValue ?? [])
const activeFilters: ComputedRef<Record<string, boolean>> = computed(() =>
  props.availableFilters.reduce(
    (acc: Record<string, boolean>, f: AvailableFilters[number]) => ({
      ...acc,
      [f.name]: selectedFilters.value.some((sf) => sf === f.name),
    }),
    {},
  ),
)
const themeVars = useThemeVars()
const onTagClick = (f: ArrayElement<AvailableFilters>) => {
  if (props.mutuallyExclusive) {
    selectedFilters.value = [f.name]
    emit('update:model-value', selectedFilters.value)
  } else {
    selectedFilters.value = selectedFilters.value.includes(f.name)
      ? selectedFilters.value.filter((sf) => sf !== f.name)
      : [...selectedFilters.value, f.name]
    emit('update:model-value', selectedFilters.value)
  }
}
const onTagClose = (f: ArrayElement<AvailableFilters>) => {
  selectedFilters.value = selectedFilters.value.filter((sf) => sf !== f.name)
  emit('update:model-value', selectedFilters.value)
}

watchEffect(() => {
  selectedFilters.value = props.modelValue ?? []
})
</script>

<style lang="scss" scoped>
.c-quick-filters__tag {
  text-transform: uppercase;
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;
  letter-spacing: 1.25px;

  &--active {
    background-color: v-bind('themeVars.primary5');
  }
}
</style>
